import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { FiscalYear, FiscalYearResponse } from "../types";

interface UseFiscalYearResult {
  year: FiscalYear | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch a single fiscal year with its periods.
 */
export function useFiscalYear(id: string | undefined): UseFiscalYearResult {
  const [year, setYear] = useState<FiscalYear | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setYear(null);
      setIsLoading(false);
      return;
    }

    const fetchYear = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await apiFetch.get<FiscalYearResponse>(
          `/api/fiscal-calendar/years/${id}`,
        );
        setYear(data.year);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setYear(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchYear();
  }, [id, fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    year,
    isLoading,
    error,
    refetch,
  };
}
